import { useMemo, useState } from "react";
import { useEnterEvent } from "../../../context/EnterEvent";
import { Error } from "../../../components";
import "./style.scss";
import { useAuth } from "../../../context/Auth";
import { useNavigate } from "react-router-dom";
import strings, { toastType } from "../../../utils/strings";
import moment from "moment";

/** columns shared by every event list (Domestic, International and General).
 * fixed-width columns take their col-lg-* size, the remaining ones use plain
 * "col" so they share what is left and never wrap onto a new row */
const eventHeaders = [
  {
    name: "Event",
    class: "col-lg-3",
    key: "eventTitle",
  },
  {
    name: "Action",
    class: "col-lg-2 text-center",
    key: "enter",
  },
  // {
  //   name: "Venue",
  //   class: "col hidden-sm",
  //   key: "venue",
  // },
  {
    name: "Information PDF",
    class: "col-lg-2 text-center",
    key: "downloads",
  },
  {
    name: "Registrations Close",
    class: "col hidden-sm",
    key: "entriesCloseTime",
  },
  {
    name: "Event Starts",
    class: "col hidden-sm",
    key: "startTime",
  },
];

const EventSelect = ({
  title,
  /** shown in place of the title while the list is open */
  heading,
  type,
  headers = eventHeaders,
}) => {
  const { onEventSelect, selectedEvent, events, setConfirmation, editId } =
    useEnterEvent();
  const [open, setOpen] = useState(false);

  const { subscriptionData, user, showToast, userCheck } = useAuth();

  const filteredEvents = useMemo(
    () => events?.filter((event) => event?.[type]),
    [events, type]
  );

  const hasSelected = useMemo(
    () => editId || filteredEvents?.find(({ id }) => id === selectedEvent?.id),
    [editId, filteredEvents, selectedEvent]
  );

  const navigate = useNavigate();

  /** the list is no longer behind a dropdown, so the login check that used
   * to guard opening it now guards the selection itself */
  const handleSelect = (event) => userCheck(() => selectEvent(event));

  const selectEvent = (event) => {
    if (editId) return;
    /** free events are open to everyone, no membership, subscription
     * validity or profile completeness check is required */
    if (event?.isFree) {
      onEventSelect?.(event);
      setOpen(false);
      return;
    }
    if (subscriptionData?.isSubscribed) {
      const subscriptionExpiryDate = subscriptionData?.expiryDate?.split(" ")?.[0]
      const eventEndDate = event?.startTime?.split(" ")?.[0]

      if (moment(subscriptionExpiryDate).isBefore(moment(eventEndDate))) {
        setConfirmation({
          title: "Your subscription will expire before the event.",
          subTitle:
            "Please renew your subscription to participate in this event.",
          yesText: "Renew Subscription",
          open: true,
          singleBtn: true,
          yesClick: () => {
            setConfirmation({ open: false });
            if (
              user?.user?.first_name &&
              user?.user?.date_of_birth &&
              user?.user?.occupation &&
              user?.user?.country_code &&
              user?.user?.country
            ) {
              navigate("/subscription");
            } else {
              navigate("/edit-profile/continue");
              showToast({
                text: strings.completeProfile,
                type: toastType.ERROR,
              });
            }
          },
        });
        return
      }
      onEventSelect?.(event);
    } else {
      setConfirmation({
        title: "",
        subTitle:
          "To enter a contest please upgrade to premium membership.",
        yesText: "Ok",
        open: true,
        singleBtn: true,
        yesClick: () => {
          setConfirmation({ open: false });

          if (
            user?.user?.first_name &&
            user?.user?.date_of_birth &&
            user?.user?.occupation &&
            user?.user?.country_code &&
            user?.user?.country
          ) {
            navigate("/subscription");
          } else {
            navigate("/edit-profile/continue");
            showToast({
              text: strings.completeProfile,
              type: toastType.ERROR,
            });
          }
        },
      });
    }
    setOpen(false);
  };

  return (
    <>
      <div
        className={`event-select-overlay ${open ? "showing-select" : ""}`}
        onClick={() => setOpen(false)}
      />
      <div
        className={
          `card shadow-xss w-100 border-0 p-0 mb-3 mt-1 event-select${open ? " open" : ""} 
          ${hasSelected ? " selected" : ""} ${editId ? "disabled-event-select" : ""}`
        }
      >
        <div className="card-body">
          <div
            className="cursor"
            onClick={() =>
              userCheck(() => {
                !editId && setOpen(!open);
              })
            }
          >
            <div className="event-select-title">
              {open && heading
                ? <strong>{heading}</strong>
                : (hasSelected && selectedEvent?.eventTitle) || title}
            </div>
            <i className="fa-solid fa-chevron-down e-select-arrow"></i>
          </div>
          <div className={`event-select-options`}>
            {filteredEvents?.length ? (
              <>
                {" "}
                <div className="row event-select-th">
                  {headers?.map(({ name, class: cls }) => (
                    <div
                      key={`th-${cls}-${name}`}
                      className={`col ${cls}`}
                    >
                      <strong>{name}</strong>
                    </div>
                  ))}
                </div>
                {filteredEvents?.map((event) => (
                  <div
                    key={event?.id}
                    className={`row custom-select-row ${selectedEvent?.id === event?.id ? "selected" : ""
                      }`}
                    onClick={() => handleSelect(event)}
                  >
                    {headers?.map(({ key, class: cls }, i) => (
                      <div key={`${key}-${event?.id}`} className={`col ${cls}`}>
                        {key === "enter" ? (
                          <button
                            type="button"
                            className={`event-select-enter${selectedEvent?.id === event?.id ? " selected" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelect(event);
                            }}
                          >
                            {selectedEvent?.id === event?.id
                              ? "Selected"
                              : "Enter"}
                          </button>
                        ) : key === "downloads" ? (
                          /* information PDF, downloading must not select
                           * the event, hence the stopPropagation */
                          <span
                            className="event-select-downloads"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {event?.posterFile ? (
                              <a
                                href={event?.posterFile}
                                target="_blank"
                                rel="noreferrer"
                                title="Information PDF"
                                download
                              >
                                <i className="fa fa-download" />
                              </a>
                            ) : (
                              <span className="font-xssss text-grey-500">
                                Pending
                              </span>
                            )}
                          </span>
                        ) : (
                          <>
                            {key?.includes("Time")
                              ? moment(event[key]).format("DD MMM yyyy")
                              : event[key]}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </>
            ) : (
              <Error
                error="No events to show"
                bgClass="bg-none"
                textClass="text-red"
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EventSelect;
